/**
 * Scrape Domain Model
 * 
 * Encapsulates business logic and validation rules for scrapes (jobs).
 * Pure business logic - no Firestore operations.
 */

export class Scrape {
  constructor(data = {}) {
    // Preserve every field from the source document so reads are loss-free —
    // components can rely on raw fields that aren't explicitly modeled below.
    // Writes are unaffected: toFirestore() only serialises the named fields.
    Object.assign(this, data);

    // Core identification
    this.id = data.id || null;
    this.jobId = data.jobId || data.id || null;
    this.name = data.name || null;
    this.displayName = data.displayName || data.name || null;
    
    // Scraper tracking
    this.scraperId = data.scraperId || null;
    this.scraperInstance = data.scraperInstance || null;
    this.createdBy = data.createdBy || null;
    
    // Mailshot relationship
    this.mailshotId = data.mailshotId || null;
    
    // Status
    this.status = data.status || 'pending'; // pending, running, completed, failed, cancelled
    this.archived = data.archived || false;
    
    // Search configuration
    this.postcodes = data.postcodes || [];
    this.searchParams = data.searchParams || {};
    
    // Property counts
    this.totalProperties = data.totalProperties || 0;
    this.successfulProperties = data.successfulProperties || 0;
    this.failedProperties = data.failedProperties || 0;
    this.processedProperties = data.processedProperties || 0;
    
    // Mailshot statistics (cached)
    this.mailshotStats = data.mailshotStats || {
      inMailshots: 0,
      forSale: 0,
      sstc: 0,
      mailshots: [],
    };
    
    // Progress tracking
    this.progress = data.progress || {
      totalPostcodes: 0,
      processedPostcodes: 0,
      totalProperties: 0,
      processedProperties: 0,
      successfulProperties: 0,
      failedProperties: 0,
    };
    
    // Error tracking
    this.error = data.error || null;
    this.errorDetails = data.errorDetails || null;
    
    // Timestamps
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.startedAt = data.startedAt || null;
    this.completedAt = data.completedAt || null;
    
    // Historical import flag
    this.isHistoricalImport = data.isHistoricalImport || false;
    this.importSource = data.importSource || null;
    
    // Result file
    this.resultFile = data.resultFile || null;
  }

  /**
   * Calculate statistics from a list of properties
   * @param {Property[]} properties - Array of Property instances
   * @returns {Object} - Statistics object
   */
  calculateStats(properties) {
    const stats = {
      total: properties.length,
      successful: properties.filter(p => p.propertyId).length,
      failed: properties.filter(p => !p.propertyId || p.error).length,
      inMailshots: properties.filter(p => p.mailshotIds && p.mailshotIds.length > 0).length,
      forSale: properties.filter(p => p.propertyStatus === 'For Sale').length,
      sstc: properties.filter(p => p.propertyStatus === 'Sold STC').length,
      underOffer: properties.filter(p => p.propertyStatus === 'Under Offer').length,
    };
    
    // Get unique mailshots these properties are in
    const mailshotSet = new Set();
    properties.forEach(property => {
      if (property.mailshotIds && property.mailshotIds.length > 0) {
        property.mailshotIds.forEach(id => mailshotSet.add(id));
      }
    });
    
    return {
      totalProperties: stats.total,
      successfulProperties: stats.successful,
      failedProperties: stats.failed,
      processedProperties: stats.total,
      mailshotStats: {
        inMailshots: stats.inMailshots,
        forSale: stats.forSale,
        sstc: stats.sstc,
        underOffer: stats.underOffer,
        mailshots: Array.from(mailshotSet),
      },
    };
  }

  /**
   * Check if scrape is complete
   * @returns {boolean} - True if scrape is complete
   */
  isComplete() {
    return this.status === 'completed' || this.status === 'failed';
  }

  /**
   * Check if scrape is running
   * @returns {boolean} - True if scrape is in progress
   */
  isRunning() {
    return this.status === 'running';
  }

  /**
   * Check if scrape failed
   * @returns {boolean} - True if scrape failed
   */
  isFailed() {
    return this.status === 'failed';
  }

  /**
   * Check if scrape is successful
   * @returns {boolean} - True if scrape completed successfully
   */
  isSuccessful() {
    return this.status === 'completed' && this.failedProperties === 0;
  }

  /**
   * Check if this scrape can be merged with another
   * @param {Scrape} other - Another scrape to check merge compatibility
   * @returns {boolean} - True if scrapes can be merged
   */
  canBeMergedWith(other) {
    // Cannot merge with self
    if (this.id === other.id) {
      return false;
    }
    
    // Both must be completed
    if (!this.isComplete() || !other.isComplete()) {
      return false;
    }
    
    // Cannot merge archived scrapes
    if (this.archived || other.archived) {
      return false;
    }
    
    return true;
  }

  /**
   * Check if scrape can be deleted
   * @returns {boolean} - True if scrape can be deleted
   */
  canBeDeleted() {
    // Can always delete (cascade will handle property references)
    // Properties might reference this scrape via jobId
    return true;
  }

  /**
   * Get list of collections this scrape affects
   * Used for cascade operations
   * @returns {string[]} - Array of collection names
   */
  getAffectedCollections() {
    const affected = ['discovery_scrapes'];
    
    // If scrape has properties, they're affected
    if (this.totalProperties > 0) {
      affected.push('discovery_properties');
    }
    
    // If scrape is in mailshots, they're affected
    if (this.mailshotStats?.mailshots?.length > 0) {
      affected.push('discovery_mailshots');
    }
    
    return affected;
  }

  /**
   * Get progress percentage
   * @returns {number} - Progress as percentage (0-100)
   */
  getProgressPercentage() {
    if (!this.progress || this.progress.totalPostcodes === 0) {
      return 0;
    }
    return Math.floor((this.progress.processedPostcodes / this.progress.totalPostcodes) * 100);
  }

  /**
   * Validate scrape data
   * @throws {Error} - If validation fails
   * @returns {boolean} - True if valid
   */
  validate() {
    if (!this.jobId) {
      throw new Error('jobId is required');
    }
    
    // Validate postcodes is array
    if (this.postcodes && !Array.isArray(this.postcodes)) {
      throw new Error('postcodes must be an array');
    }
    
    // Validate status
    const validStatuses = ['pending', 'running', 'completed', 'failed', 'cancelled'];
    if (this.status && !validStatuses.includes(this.status)) {
      throw new Error(`Invalid status: ${this.status}. Must be one of: ${validStatuses.join(', ')}`);
    }
    
    // Validate counts are non-negative
    if (this.totalProperties < 0 || this.successfulProperties < 0 || this.failedProperties < 0) {
      throw new Error('Property counts cannot be negative');
    }
    
    return true;
  }

  /**
   * Serialize to Firestore document
   * @returns {Object} - Firestore document data
   */
  toFirestore() {
    const doc = {
      jobId: this.jobId,
      status: this.status,
      archived: this.archived,
      totalProperties: this.totalProperties,
      successfulProperties: this.successfulProperties,
      failedProperties: this.failedProperties,
      processedProperties: this.processedProperties,
      mailshotStats: this.mailshotStats,
      postcodes: this.postcodes,
    };
    
    // Optional fields
    if (this.name) doc.name = this.name;
    if (this.displayName) doc.displayName = this.displayName;
    if (this.scraperId) doc.scraperId = this.scraperId;
    if (this.scraperInstance) doc.scraperInstance = this.scraperInstance;
    if (this.createdBy) doc.createdBy = this.createdBy;
    if (this.mailshotId) doc.mailshotId = this.mailshotId;
    if (this.searchParams) doc.searchParams = this.searchParams;
    if (this.progress) doc.progress = this.progress;
    if (this.error) doc.error = this.error;
    if (this.errorDetails) doc.errorDetails = this.errorDetails;
    if (this.createdAt) doc.createdAt = this.createdAt;
    if (this.updatedAt) doc.updatedAt = this.updatedAt;
    if (this.startedAt) doc.startedAt = this.startedAt;
    if (this.completedAt) doc.completedAt = this.completedAt;
    if (this.isHistoricalImport) doc.isHistoricalImport = this.isHistoricalImport;
    if (this.importSource) doc.importSource = this.importSource;
    if (this.resultFile) doc.resultFile = this.resultFile;
    
    return doc;
  }

  /**
   * Deserialize from Firestore document
   * @param {Object} doc - Firestore document snapshot
   * @returns {Scrape} - Scrape instance
   */
  static fromFirestore(doc) {
    if (!doc) return null;
    
    const data = doc.data ? doc.data() : doc;
    const id = doc.id || data.id;
    
    return new Scrape({
      id,
      ...data
    });
  }

  /**
   * Create a plain object for display (removes internal fields)
   * @returns {Object} - Plain object suitable for JSON serialization
   */
  toJSON() {
    return {
      id: this.id,
      jobId: this.jobId,
      name: this.name,
      status: this.status,
      totalProperties: this.totalProperties,
      successfulProperties: this.successfulProperties,
      failedProperties: this.failedProperties,
      mailshotStats: this.mailshotStats,
      createdAt: this.createdAt,
      completedAt: this.completedAt,
    };
  }
}
